import { Card } from '@popsure/dirty-swan';
import classNames from 'classnames';
import dayjs from 'dayjs';
import documentBoxIcon from 'features/claimsV2/icons/document-box.svg';
import { getClaimTypeMapping } from 'features/claimsV2/utils';
import {
  claimStatusesClassMapping,
  ClaimStatusWithAPV,
  ClaimType,
  getClaimStatusesTextMapping,
} from 'models/claims';
import { InsuranceTypes } from 'models/insurances/types';
import {
  getTitleMapping,
  imageTypeMapping,
} from 'models/insurances/types/mapping';
import { Link } from 'react-router-dom';
import { useSafeTranslation } from 'shared/i18n';

import { StatusBadge } from '../statusBadge';
import styles from './ClaimCard.module.scss';

interface Props {
  date: string;
  status: ClaimStatusWithAPV;
  subTitle?: string;
  insuranceType: InsuranceTypes;
  claimTypes?: ClaimType[];
  to: string;
}

export const ClaimCard = ({
  date,
  status,
  subTitle,
  insuranceType,
  claimTypes,
  to,
}: Props) => {
  const { t } = useSafeTranslation();
  const hasClaimTypes = claimTypes && claimTypes.length > 0;
  const claimTypeMapping = hasClaimTypes
    ? getClaimTypeMapping(t, {
        insuranceType,
        claimType: claimTypes[0].claimType,
      })
    : null;

  return (
    <Card
      data-cy="claim-card"
      as={Link}
      to={to}
      actionIcon={
        <div className={styles.mobileHidden}>
          <StatusBadge
            badgeColor={claimStatusesClassMapping[status]}
            title={getClaimStatusesTextMapping(t)[status]}
          />
        </div>
      }
      title={
        hasClaimTypes && claimTypeMapping ? (
          <>
            {claimTypeMapping}
            {claimTypes.length > 1 && <> +{claimTypes.length - 1}</>}
          </>
        ) : (
          <>
            {getTitleMapping(t)[insuranceType]}{' '}
            {t('claims.claimsCard.title.post.claim', 'claim')}
          </>
        )
      }
      description={
        <div className={styles.info}>
          <div
            className={classNames(
              'p-p--small tc-grey-700 fw-bold mr16',
              styles.mobileHidden
            )}
          >
            {dayjs(date).format('DD MMM YYYY')}
          </div>

          {subTitle && (
            <div className="d-flex p-p--small tc-grey-500">{subTitle}</div>
          )}

          <div
            className={classNames(
              'mt16 ai-center jc-between',
              styles.mobileDisplay
            )}
          >
            <StatusBadge
              badgeColor={claimStatusesClassMapping[status]}
              title={getClaimStatusesTextMapping(t)[status]}
            />

            <div className={classNames('p-p--small tc-grey-700 fw-bold')}>
              {dayjs(date).format('DD MMM YYYY')}
            </div>
          </div>
        </div>
      }
      icon={
        <img
          className="br8"
          width={48}
          src={imageTypeMapping[insuranceType] ?? documentBoxIcon}
          alt="insurance icon"
        />
      }
      classNames={{
        buttonWrapper: classNames('mb16', styles.wrapper),
        icon: styles.icon,
      }}
      titleVariant="medium"
      dropShadow={false}
      showActionIcon
      density="balanced"
    />
  );
};
